// THE OWNER'S CORRECTIONS AND ANSWERS, HELD IN ONE PLACE AND SENT WHOLE (WEB-PARITY-1C, F-WP1A-1; WEB-PARITY-2A).
//
// `v2ResolveProperty` `record` lays the call's own corrections over the county record, and EACH CALL CARRIES THE WHOLE
// SET: a key the call omits is the county's figure again (`functions/src/v2/contract/schema.ts`, the `record` op). So a
// call that sends nothing — which `/details`, `/you` and `/letter` all made on every render until 1C — quietly put every
// figure the owner had fixed back to the county's, and the letter was then built on the county's.
//
// SINCE 2.15.0 THE SAME IS TRUE OF THE OWNER'S ANSWERS ABOUT THE RECORD (WEB-PARITY-2A): "Yes, that's it" to a near miss,
// the parcel they picked from a building's list, and "Enter details from my notice" after an outage. The server adopts a
// held record ONLY on the answer the call carries — SILENCE ADOPTS NOTHING — so a later call that forgot the answer would
// take the owner's own home back off the page. Every call sends both, here.
//
// THE ONE PLACE IS THE SERVER'S OWN SUBJECT. The stored subject echoes the set it was built from (`corrections`, and the
// answers on `record`), and every response's appeal snapshot carries that subject. So nothing is kept in the browser that
// could disagree with it: `correctionsOn` and `answersOn` read the set back off the subject, and
// `recordKeepingCorrections` reads the snapshot first (`v2Outcomes` `list`, which writes nothing) and sends that set with
// the call. `/details` adds the owner's edits to the same set before it sends (`withCorrection`, `withAnswers`).

import type { ResolvePropertyRecordInput, ResolvePropertyResponse, SubjectView, V2Result } from "@contract";
import type { Transport } from "../transport/types";
import { readSnapshot } from "./snapshot";

export type Corrections = NonNullable<ResolvePropertyRecordInput["corrections"]>;

/** The owner's answers about the record (WEB-PARITY-2A): the request's own three keys. */
export type RecordAnswers = Pick<ResolvePropertyRecordInput, "confirmations" | "unitChoice" | "withoutLookup">;

type FactLike = { value: unknown; source: string } | null | undefined;

/** A fact the owner supplied: corrected over a record, or entered where there was none. */
function owners(fact: FactLike): boolean {
  return fact !== null && fact !== undefined && (fact.source === "ownerCorrected" || fact.source === "ownerEntered");
}

/**
 * THE SET OF CORRECTIONS THE STORED SUBJECT CARRIES, to resend with the next call.
 *
 * Since 2.15.0 the subject says so itself (`corrections`, the set exactly as the server applied it). A subject stored
 * before then does not, and its set is read off its facts as 1C read it: the property type is the one fact whose
 * `ownerEntered` is NOT a correction there — it is the owner's pick on `/property`, which the server keeps on the address
 * phase — so only an `ownerCorrected` type is resent.
 */
export function correctionsOn(subject: SubjectView | null): Corrections {
  if (subject === null) return {};
  if (subject.corrections !== undefined) return { ...subject.corrections };
  const c: Corrections = {};
  if (owners(subject.sqft)) c.sqft = subject.sqft!.value;
  if (owners(subject.beds)) c.beds = subject.beds!.value;
  if (owners(subject.baths)) c.baths = subject.baths!.value;
  if (owners(subject.assessedValue)) c.assessedValue = subject.assessedValue!.value;
  if (owners(subject.marketValue)) c.marketValue = subject.marketValue!.value;
  if (owners(subject.parcel)) c.parcel = subject.parcel!.value;
  if (subject.propertyType?.source === "ownerCorrected") c.propertyType = subject.propertyType.value;
  if (owners(subject.yearBuilt)) c.yearBuilt = subject.yearBuilt!.value;
  if (owners(subject.lotSizeSqft)) c.lotSizeSqft = subject.lotSizeSqft!.value;
  return c;
}

/** THE OWNER'S ANSWERS THE STORED SUBJECT WAS BUILT ON (WEB-PARITY-2A), to resend: none on a subject stored before 2.15.0,
 *  which could carry none. */
export function answersOn(subject: SubjectView | null): RecordAnswers {
  const r = subject?.record;
  if (r === undefined || r === null) return {};
  const out: RecordAnswers = {};
  if (r.confirmed !== null) out.confirmations = { record: r.confirmed };
  if (r.unitChoice !== null) out.unitChoice = r.unitChoice;
  if (r.withoutLookup) out.withoutLookup = true;
  return out;
}

/** The set with one key changed, or removed (`undefined`): a removed key is the county's figure again. */
export function withCorrection<K extends keyof Corrections>(set: Corrections, key: K, value: Corrections[K] | undefined): Corrections {
  const next: Corrections = { ...set };
  if (value === undefined) delete next[key];
  else next[key] = value;
  return next;
}

/** The answers with some changed; an `undefined` removes one, which is no answer. */
export function withAnswers(answers: RecordAnswers, patch: { [K in keyof RecordAnswers]?: RecordAnswers[K] | undefined }): RecordAnswers {
  const next: RecordAnswers = { ...answers };
  for (const key of Object.keys(patch) as (keyof RecordAnswers)[]) {
    const value = patch[key];
    if (value === undefined) delete next[key];
    else (next as Record<string, unknown>)[key] = value;
  }
  return next;
}

/** The request for this set: no key the owner has not given, so an uncorrected appeal sends what it always did. */
export function recordInput(appealId: string, set: Corrections, answers: RecordAnswers = {}): ResolvePropertyRecordInput {
  return {
    appealId,
    ...(Object.keys(set).length === 0 ? {} : { corrections: set }),
    ...answers,
  };
}

/**
 * `record`, resending every correction and answer the owner has made. The snapshot is read first; if it cannot be read,
 * that failure is the answer — a bare `record` in its place would be the very call that loses the owner's figures.
 *
 * `extra` is `/details`' own: "Enter details from my notice" after an outage, before any subject exists to carry it.
 */
export async function recordKeepingCorrections(
  transport: Transport, appealId: string, extra: RecordAnswers = {},
): Promise<V2Result<ResolvePropertyResponse>> {
  const snapshot = await readSnapshot(transport, appealId);
  if (!snapshot.ok) return snapshot;
  const subject = snapshot.response?.subject ?? null;
  return transport.v2ResolveProperty.record(recordInput(appealId, correctionsOn(subject), { ...answersOn(subject), ...extra }));
}
